/* eslint-disable @next/next/no-img-element */
import styles from './style.module.scss';
import { useContext, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { getMonths } from 'shared/constants';
import { useTranslation } from 'react-i18next';
import { getAPIClient } from '@/services/axios';
import EventsContext from 'context/EventsListingsProvider';
import Loader from 'components/atoms/Loader';
import {
  getNewDate,
  getEventTimeDateInfo,
  getHoursAndMinutesFromISODate,
  getTimezonesData,
  getEventTimezoneOffset,
  convertTimezoneBasedOnDate
} from 'shared/utils';
import NoResults from 'components/atoms/NoResults';
import { useEventsContext } from 'hooks/useContexts';
import useRouterStuff from 'hooks/useRouterStuff';
import Toolbar from 'componentsV2/pages/EventsCalendarPage/CalendarComponents/Toolbar';
import EventModalShow from 'componentsV2/molecules/Modals/EventModals/EventModalShow';
import EventModalModeration from 'componentsV2/molecules/Modals/EventModals/EventModalModeration';

const ACCEPTED = 'ACCEPTED';
const REQUIRED_REVIEW = 'REQUIRED_REVIEW';
const REJECTED = 'REJECTED';

export default function EventPartnerList({
  events,
  moderation = false,
  listEvents = false,
  editEvents = false,
  viewOnly = false,
  providers,
  eventTypes,
  regions,
  products,
  partnerId,
  providerId,
  userPermissions,
  isSiteAdminPage = false,
  partnerName = '',
  partnerSlug = '',
  currentSubTabCalendar,
  usersTimezoneData,
  doNotShowViewAllPartner = false
}) {
  const {
    selectedEvent,
    setSelectedEvent,
    allTimezones,
    view,
    setView,
    currentDate,
    setCurrentDate,
    selectedTab,
    setSelectedTab,
    usersTimezoneLocation
  } = useEventsContext();
  const { t } = useTranslation();
  const months = getMonths(t);

  const [isOpen, setIsOpen] = useState(selectedEvent !== null);
  const [isLoading, setIsLoading] = useState(false);
  const [rangeDate, setRangeDate] = useState<{ startDate: any; endDate: any }>({
    startDate: null,
    endDate: null
  });

  const { router, searchParamsObj, queryParams } = useRouterStuff();

  const yearOfEvents = moderation
    ? Array.of(
        events
          .map((e) => {
            return new Date(e.startDate).getFullYear();
          })
          .filter((item, pos, arr) => arr.indexOf(item) == pos)
      ).sort((a, b) => b - a)
    : [];

  const timezonesData = getTimezonesData(allTimezones, usersTimezoneLocation);

  const selectArrowStyle = {
    height: '19px',
    width: '243px'
  };

  const getCurrentDimension = () => {
    return {
      width: window.innerWidth,
      height: window.innerHeight
    };
  };
  const [screenSize, setScreenSize] = useState(getCurrentDimension());

  useEffect(() => {
    const updateDimension = () => {
      setScreenSize(getCurrentDimension());
    };
    window.addEventListener('resize', updateDimension);

    return () => {
      window.removeEventListener('resize', updateDimension);
    };
  }, [screenSize]);

  useEffect(() => {
    const { eventId } = searchParamsObj;
    if (eventId) {
      setSelectedEvent(events.find((el) => el.id === Number(eventId)));
      queryParams({
        set: {
          eventId: eventId
        }
      });
      setIsOpen(true);
    }
  }, [searchParamsObj.eventId]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedEvent(null);
      queryParams({ del: ['eventId'] });
    }
  }, [isOpen]);
  const getIsEventInRange = (event, rangeDate) => {
    const eventTimezoneOffset = getEventTimezoneOffset({
      eventTimezone: event.timezone,
      timezonesData
    });
    const { startDate } = getEventTimeDateInfo({
      event,
      timezoneHoursOffset:
        usersTimezoneData.currentTimeOffsetInMinutes / 60 -
        eventTimezoneOffset / 60
    });
    return startDate >= rangeDate.startDate && startDate <= rangeDate.endDate;
  };

  let monthHeader;
  let yearHeader;

  const getEventTimeInfo = (event) => {
    const eventTimezoneOffset = getEventTimezoneOffset({
      eventTimezone: event.timezone,
      timezonesData
    });

    const { startDate, endDate } = getEventTimeDateInfo({
      event,
      timezoneHoursOffset:
        usersTimezoneData.currentTimeOffsetInMinutes / 60 -
        eventTimezoneOffset / 60
    });

    const eventYear = startDate.split('T')[0].split('-')[0];
    const eventMonth = startDate.split('T')[0].split('-')[1];

    const differentDays = startDate.split('T')[0] !== endDate.split('T')[0];

    const eventTimeLabel = event.allDayEvent
      ? t('all_day')
      : getHoursAndMinutesFromISODate(startDate);

    return {
      eventYear,
      eventMonth,
      startDate,
      endDate,
      differentDays,
      eventTimeLabel
    };
  };
  const getEventImage = async (event) => {
    const res = await getAPIClient().get<{image: string}>(`/events/image/${event.id}`);
    return res.data.image;
  }
  const EventListComponent = ({
    showMonthHeader,
    month,
    eventYear,
    startDate,
    endDate,
    differentDays,
    event,
    eventTimeLabel
  }) => {
    return (
      <>
        {showMonthHeader && (
          <>
            <h1>
              {month.name} {eventYear}
            </h1>

            <div className={styles.line} />
          </>
        )}
        <div className={styles.event_wrapper} key={`event_${event.id}`}>
          <div className={styles.event_info}>
            <div className={styles.event_date_wrapper}>
              <img
                src={event.contentStatus === REQUIRED_REVIEW
                      ? '/images/calendar_events_yellow.svg'
                      : event.contentStatus === REJECTED
                      ? '/images/calendar_events_red.svg'
                      : '/images/calendar_events.svg'
                }
                alt="calendar"
                className={styles.calendar_img}
              />
              <div className={styles.event_date}>
                <span className={styles.date}>
                  {`${startDate.split('T')[0].split('-')[2]} ${
                    differentDays
                      ? ` - ${endDate.split('T')[0].split('-')[2]}`
                      : ''
                  } ${
                    months?.find(
                      (el) => el.number === endDate.split('T')[0].split('-')[1]
                    )?.short
                  }, ${endDate.split('T')[0].split('-')[0]}`}
                </span>
                <span className={styles.time}>
                  {eventTimeLabel}{' '}
                  {convertTimezoneBasedOnDate(
                    usersTimezoneData.label,
                    startDate,
                    timezonesData,
                    true
                  )}{' '}
                </span>
              </div>
            </div>
            {event?.Partner?.name?.length > 0 && (
              <div className={styles.partner_wrapper}>
                <span>{t('Partner')}</span>
                <span className={styles.partner_name}>
                  {event.Partner.name}
                </span>
              </div>
            )}
            {isSiteAdminPage && event?.LocatorCompany?.name?.length > 0 && (
              <div className={styles.partner_wrapper}>
                <span>{t('provider')}</span>
                <span className={styles.partner_name}>
                  {event.LocatorCompany.name}
                </span>
              </div>
            )}
            <div className={styles.event_title}>
              <span>{event.title}</span>
              <span className={styles.tagline}>
                {event.tagline.length > 15 && screenSize.width <= 768
                  ? `${event.tagline.slice(0, 15)}...`
                  : event.tagline}
              </span>
            </div>
          </div>
          <div className={styles.event_button}>
            {(moderation || event.contentStatus !== ACCEPTED) &&
              (event.contentStatus === REQUIRED_REVIEW ? (
                <span className={styles.pending}>({t('pending')})</span>
              ) : (
                <span className={styles.change}>({t('required_change')})</span>
              ))}
            <span
              className={styles.btn}
              onClick={ async () => {
                const evImg = await getEventImage(event);
                queryParams({
                  set: {
                    eventId: event.id
                  }
                });
                setSelectedEvent((e) => {
                  e = event;
                  e.image = evImg;
                  e.locatorCompanyId = event?.LocatorCompany?.id
                  e.regionId = e.region;
                  e.type = e.EventTypes;
                  return e;
                });
                setIsOpen(true);
              }}
            >
              {moderation || event.contentStatus !== ACCEPTED
                ? `${t('moderation')} >`
                : editEvents ||
                  (isSiteAdminPage &&
                    currentSubTabCalendar !== 'pe' &&
                    currentSubTabCalendar !== 'vcsp')
                ? `${t('edit_event')} >`
                : `${t('view_event')} >`}
            </span>
          </div>
        </div>
      </>
    );
  };

  const ListEventsByMonthYear = ({ year }) => {
    setIsLoading(true);
    const areThereEvents = events.some((event) => {
      if (moderation)
        return (
          event.contentStatus !== 'ACCEPTED' &&
          getIsEventInRange(event, {
            startDate: `${
              yearOfEvents ? yearOfEvents[yearOfEvents?.length - 1] : ''
            }-01-01T00:00:00.000Z`,
            endDate: `${
              yearOfEvents ? yearOfEvents[0] : ''
            }-12-31T00:00:00.000Z`
          })
        );

      return (
        listEvents &&
        (isSiteAdminPage ||
          (!isSiteAdminPage && event.contentStatus === 'ACCEPTED')) &&
        getIsEventInRange(event, rangeDate)
      );
    });

    setIsLoading(false);

    return (
      <div key={`year_${year}`}>
        {areThereEvents ? (
          months.map((month, index) => {
            const [yearStart, yearEnd] = [year, year];
            const monthEvent = events
              .filter((event) => {
                if (moderation)
                  return (
                    event.contentStatus !== 'ACCEPTED' &&
                    getIsEventInRange(event, {
                      startDate: `${year}-01-01T00:00:00.000Z`,
                      endDate: `${year}-12-31T00:00:00.000Z`
                    })
                  );

                return (
                  listEvents &&
                  (isSiteAdminPage ||
                    (!isSiteAdminPage && event.contentStatus === 'ACCEPTED')) &&
                  getIsEventInRange(event, rangeDate)
                );
              })
              .filter((event) =>
                getIsEventInRange(event, {
                  startDate:
                    getNewDate({
                      date: `${year}-01-01T00:00:00.000Z`,
                      usersTimezoneLocation,
                      getISODateFromUsersTimezone: true
                    }).split('T')[0] + 'T00:00:00.000Z',
                  endDate:
                    getNewDate({
                      date: `${year}-12-31T00:00:00.000Z`,
                      usersTimezoneLocation,
                      getISODateFromUsersTimezone: true
                    }).split('T')[0] + 'T23:59:59.999Z'
                })
              )
              .filter((event) => {
                const { eventMonth, eventYear } = getEventTimeInfo(event);

                return (
                  Number(eventMonth) === Number(month.number) &&
                  Number(eventYear) >= Number(yearStart) &&
                  Number(eventYear) <= Number(yearEnd)
                );
              });

            if (monthEvent?.length > 0) {
              return (
                <div key={`event_${month.name}_${index}`}>
                  {monthEvent.map((event, index) => {
                    const {
                      eventYear,
                      startDate,
                      endDate,
                      differentDays,
                      eventTimeLabel
                    } = getEventTimeInfo(event);

                    const showMonthHeader =
                      monthHeader !== month.name || yearHeader !== year;

                    monthHeader = month.name;
                    yearHeader = year;

                    return listEvents && screenSize.width <= 1208 ? (
                      <div
                        className={styles.event_box}
                        key={`event_${event.id}`}
                      >
                        <EventListComponent
                          showMonthHeader={showMonthHeader}
                          month={month}
                          eventYear={year}
                          startDate={startDate}
                          endDate={endDate}
                          differentDays={differentDays}
                          event={event}
                          eventTimeLabel={eventTimeLabel}
                        />
                        <div className={styles.event_lower}>
                          {screenSize.width < 590 ? (
                            <div className={styles.event_title}>
                              <span>
                                {event.title.length > 50
                                  ? `${event.title.slice(0, 50)}...`
                                  : event.title}
                              </span>
                              <span className={styles.tagline}>
                                {event.tagline.length > 50
                                  ? `${event.tagline.slice(0, 50)}...`
                                  : event.tagline}
                              </span>
                            </div>
                          ) : (
                            <div className={styles.event_title}>
                              <span>
                                {event.title.length > 80
                                  ? `${event.title.slice(0, 80)}...`
                                  : event.title}
                              </span>
                              <span className={styles.tagline}>
                                {event.tagline.length > 80
                                  ? `${event.tagline.slice(0, 80)}...`
                                  : event.tagline}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <EventListComponent
                        showMonthHeader={showMonthHeader}
                        month={month}
                        eventYear={eventYear}
                        startDate={startDate}
                        endDate={endDate}
                        differentDays={differentDays}
                        event={event}
                        eventTimeLabel={eventTimeLabel}
                      />
                    );
                  })}
                </div>
              );
            }
          })
        ) : (
          <NoResults text={t('no_events_list')} sadImage />
        )}
      </div>
    );
  };

  return isLoading ? (
    <Loader />
  ) : (
    <>
      <div
        className={styles.wrapper}
        style={
          screenSize.width < 500 ? { width: '120%', marginLeft: '-30px' } : {}
        }
      >
        <div className={styles.header}>
          {!moderation && !listEvents && <span>Upcoming Partner Events</span>}
        </div>
        <div
          className={
            moderation || listEvents
              ? styles.months_wrapper_mod
              : styles.months_wrapper
          }
        >
          {!moderation && (
            <Toolbar
              setView={setView}
              view={view}
              setSelectedTab={setSelectedTab}
              selectedTab={selectedTab}
              customStyles={selectArrowStyle}
              currentDate={currentDate}
              setCurrentDate={setCurrentDate}
              setRangeDate={setRangeDate}
              seeYearly
            />
          )}

          {moderation ? (
            yearOfEvents.map((year, index) => (
              <ListEventsByMonthYear year={year} />
            ))
          ) : (
            <ListEventsByMonthYear
              year={
                rangeDate?.startDate !== null
                  ? rangeDate?.startDate?.split('T')[0].split('-')[0]
                  : new Date().getFullYear()
              }
            />
          )}
          {!moderation && !listEvents && <div className={styles.line} />}
        </div>
      </div>
      {isOpen && selectedEvent ? (
        <>
          {viewOnly &&
          !(isSiteAdminPage && selectedEvent.contentStatus !== ACCEPTED) ? (
            <EventModalShow
              isOpen={isOpen}
              changeSetOpen={setIsOpen}
              doNotShowViewAllPartner={doNotShowViewAllPartner}
            />
          ) : (
            <EventModalModeration
              isOpen={isOpen}
              changeSetOpen={setIsOpen}
              isModeration={
                moderation ||
                (isSiteAdminPage && selectedEvent.contentStatus !== ACCEPTED)
              }
              partnerId={partnerId}
              providerId={providerId}
              editEvents={editEvents}
              partnerName={partnerName}
              partnerSlug={partnerSlug}
              eventTypes={eventTypes}
              regions={regions}
              products={products}
              providers={providers}
              isSiteAdminPage={isSiteAdminPage}
              userPermissions={userPermissions}
              newEventsCreate={undefined}
              initialEventState={undefined}
            />
          )}
        </>
      ) : null}
    </>
  );
}
